import { PlusOutlined } from '@ant-design/icons';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Form, List } from 'antd';
import type { FC } from 'react';
import React from 'react';

import type { Picture } from 'app/components/commons/Uploader/Types';

import { Headline } from './Headline/Headline';
import './Headlines.scss';

export const Headlines: FC<IProps> = ({
  mode,
  values,
  setFieldsValue,
  move,
}) => {
  const isEmpty =
    !values?.headlines || values.headlines.every((h) => !h.featured);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  return (
    <div className="headlines">
      <Form.List name="headlines">
        {(fields, { add, remove, move: moveField }) => {
          const items = mode === 'edit' || !isEmpty ? fields : [];
          const itemIds = items.map((field) => field.key);

          const handleDragEnd = ({ active, over }: DragEndEvent) => {
            if (!over || active.id === over.id) {
              return;
            }

            const oldIndex = items.findIndex(
              (field) => field.key === active.id
            );
            const newIndex = items.findIndex((field) => field.key === over.id);

            if (oldIndex === -1 || newIndex === -1) {
              return;
            }

            moveField(oldIndex, newIndex);
            move({ oldIndex, newIndex });
          };

          return (
            <>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={itemIds}
                  strategy={verticalListSortingStrategy}
                >
                  <List
                    dataSource={items}
                    renderItem={(item, index) =>
                      mode === 'edit' || values?.headlines?.[index].featured ? (
                        <Headline
                          field={item}
                          dndId={item.key}
                          mode={mode}
                          fieldIndex={index}
                          remove={remove}
                          values={values}
                          setFieldsValue={setFieldsValue}
                        />
                      ) : (
                        <div />
                      )
                    }
                    rowKey={(item) => item.key.toString()}
                    bordered
                  />
                </SortableContext>
              </DndContext>
              {mode === 'edit' && (
                <div
                  onClick={() => add({ featured: true })}
                  className="headlines__add-button"
                >
                  <PlusOutlined />
                  <span className="text">Add</span>
                </div>
              )}
            </>
          );
        }}
      </Form.List>
    </div>
  );
};

export const SortableHeadlines: FC<ISortableProps> = (props) => (
  <Headlines {...props} />
);

type HeadlineForm = {
  headlines?: {
    emoji: Picture;
    valueProposition: string;
    experienceId: number;
    featured: boolean;
    title?: string;
  }[];
};

type IProps = {
  mode: string;
  values?: HeadlineForm;
  setFieldsValue: (values: Record<string, unknown>) => void;
  move: ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => void;
};

type ISortableProps = IProps;
